import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import {
  DatePicker,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
  UserNav,
  Select,
  AutocompleteSelect
} from '../../components';
import { TopbarContainer } from '../../containers';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import css from './BusinessAvailabilityPage.css';
import classNames from 'classnames';
import truncate from 'lodash/truncate';

const date = new Date();
const currentYear = date.getFullYear();
const currentMonth = date.getMonth();
const firstDay = new Date(currentYear, currentMonth, 2).toISOString().split('T')[0];
const lastDay = new Date().toISOString().split('T')[0];

const DIMENSIONS = [
  { value: 'provider', label: 'Sklep' },
  { value: 'businessCategory', label: 'Kategoria' },
  { value: 'catalogItemId', label: 'Catalog Item' },
];
const NONE_DIM = { value: '', label: '--brak--' };

function getAllDatesInRange(start, end) {
  const dates = [];
  let current = new Date(start);
  const last = new Date(end);
  while (current <= last) {
    dates.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

export const BusinessAvailabilityPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    location,
    history,
    onLoadData,
    currentUserProviders
  } = props;

  const { start = firstDay, end = lastDay, dim1 = 'provider', dim2 = '', dim3 = '' } = parse(location.search);

  // Stan selectów wymiarów
  const [dimensions, setDimensions] = useState([
    dim1 || 'provider',
    dim2 || '',
    dim3 || '',
  ]);
  const [pendingDimensions, setPendingDimensions] = useState(dimensions);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredCell, setHoveredCell] = useState({ row: null, col: null });
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredCol, setHoveredCol] = useState(null);

  // Obsługa zmiany wymiaru
  const handleDimensionChange = (idx, value) => {
    // Nie pozwól na powtórzenia
    const newDims = [...dimensions];
    newDims[idx] = value;
    // Jeśli wybrano '--brak--', kolejne też ustaw na brak
    for (let i = idx + 1; i < 3; i++) {
      if (!newDims[idx]) newDims[i] = '';
    }
    setDimensions(newDims);
    // Buduj obiekt z tylko niepustymi wymiarami
    const urlParams = { start, end };
    if (newDims[0]) urlParams.dim1 = newDims[0];
    if (newDims[1]) urlParams.dim2 = newDims[1];
    if (newDims[2]) urlParams.dim3 = newDims[2];
    history.push(
      createResourceLocatorString(
        'BusinessAvailabilityPage',
        routeConfiguration(),
        {},
        urlParams
      )
    );
  };

  const handlePendingDimensionChange = (idx, value) => {
    const newDims = [...pendingDimensions];
    newDims[idx] = value;
    for (let i = idx + 1; i < 3; i++) {
      if (!newDims[idx]) newDims[i] = '';
    }
    setPendingDimensions(newDims);
  };

  const handleFiltersSubmit = () => {
    setDimensions(pendingDimensions);
    // Buduj urlParams jak dotychczas
    const urlParams = { start, end };
    if (pendingDimensions[0]) urlParams.dim1 = pendingDimensions[0];
    if (pendingDimensions[1]) urlParams.dim2 = pendingDimensions[1];
    if (pendingDimensions[2]) urlParams.dim3 = pendingDimensions[2];
    history.push(
      createResourceLocatorString(
        'BusinessAvailabilityPage',
        routeConfiguration(),
        {},
        urlParams
      )
    );
  };

  // Obsługa zmiany daty
  const handleDateRangeSubmit = ({ start: newStart, end: newEnd }) => {
    // Buduj obiekt z tylko niepustymi wymiarami
    const urlParams = { start: newStart, end: newEnd };
    if (dimensions[0]) urlParams.dim1 = dimensions[0];
    if (dimensions[1]) urlParams.dim2 = dimensions[1];
    if (dimensions[2]) urlParams.dim3 = dimensions[2];
    history.push(
      createResourceLocatorString(
        'BusinessAvailabilityPage',
        routeConfiguration(),
        {},
        urlParams
      )
    );
  };

  const buildApiParams = () => {
    const params = { start, end, countryId: 'pl' };
    const includes = [];
    dimensions.forEach(dim => {
      if (dim === 'provider') {
        params.providerId = '*';
        includes.push('provider');
      }
      if (dim === 'businessCategory') {
        params.businessCategory = '*';
      }
      if (dim === 'catalogItemId') {
        params.catalogItemId = '*';
        includes.push('catalogItem');
      }
    });
    if (includes.length > 0) params.include = includes;
    return params;
  };

  // Pobierz dane z API dla wszystkich wildcardów
  useEffect(() => {
    setLoading(true);
    setError(null);
    onLoadData(buildApiParams())
      .then(res => {
        setData(res.data);
        setLoading(false);
      })
      .catch(e => {
        setError(e?.message || 'Błąd ładowania danych');
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [start, end, dimensions]);

  // Przetwarzanie danych do pivotu
  const getPivot = () => {
    if (!data) return { rows: [], dates: [] };
    // Zbuduj mapę included
    const includedMap = {};
    if (data.included) {
      data.included.forEach(item => {
        if (!includedMap[item.type]) includedMap[item.type] = {};
        includedMap[item.type][item.id] = item;
      });
    }
    // Zbierz wszystkie daty z zakresu
    const allDates = getAllDatesInRange(start, end);
    // Zbierz unikalne kombinacje wymiarów
    const dims = dimensions.filter(Boolean);
    const rowsMap = new Map();
    data.data.forEach(row => {
      const attrs = row.attributes;
      const rel = row.relationships || {};
      const key = dims.map(dim => {
        if (dim === 'provider') {
          const id = rel.provider?.data?.id || attrs.provider?.id || attrs.provider;
          return includedMap['provider']?.[id]?.attributes?.name || id || '-';
        }
        if (dim === 'businessCategory') return attrs.businessCategory || '-';
        if (dim === 'catalogItemId') {
          const id = rel.catalogItem?.data?.id || attrs.catalogItem?.data?.id || attrs.catalogItemId;
          return includedMap['catalog-item']?.[id]?.attributes?.title || id || '-';
        }
        return '-';
      }).join('||');
      if (!rowsMap.has(key)) {
        rowsMap.set(key, { dims: dims.map(dim => {
          if (dim === 'provider') {
            const id = rel.provider?.data?.id || attrs.provider?.id || attrs.provider;
            return includedMap['provider']?.[id]?.attributes?.name || id || '-';
          }
          if (dim === 'businessCategory') return attrs.businessCategory || '-';
          if (dim === 'catalogItemId') {
            const id = rel.catalogItem?.data?.id || attrs.catalogItem?.data?.id || attrs.catalogItemId;
            return includedMap['catalog-item']?.[id]?.attributes?.title || id || '-';
          }
          return '-';
        }), values: {} });
      }
      const rowObj = rowsMap.get(key);
      const prev = rowObj.values[attrs.date] || { booked: 0, available: 0 };
      rowObj.values[attrs.date] = {
        booked: prev.booked + Number(attrs.booked || 0),
        available: prev.available + Number(attrs.available || 0),
      };
    });
    // Uzupełnij brakujące daty zerami
    const rows = Array.from(rowsMap.values()).map(row => {
      allDates.forEach(date => {
        if (row.values[date] === undefined) {
          row.values[date] = { booked: 0, available: 0 };
        }
      });
      return row;
    });
    return { rows, dates: allDates, dims };
  };

  const pivot = getPivot();

  const weekendIndexes = pivot.dates
    .map((date, idx) => {
      const d = new Date(date);
      return (d.getDay() === 0 || d.getDay() === 6) ? idx : null;
    })
    .filter(idx => idx !== null);

  // Opcje do selectów wymiarów
  const dimOptions = [NONE_DIM, ...DIMENSIONS.filter(dim => !dimensions.includes(dim.value))];
  const getOptions = idx => [NONE_DIM, ...DIMENSIONS.filter(dim => !dimensions.slice(0, idx).includes(dim.value))];

  const stickyCount = pivot?.dims?.length || 0;
  const minTableWidth = stickyCount * 100 + (pivot?.dates?.length || 0) * 48;

  return (
    <Page title={intl.formatMessage({ id: 'BusinessAvailabilityPage.title', defaultMessage: 'Pivot dostępności' })} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            currentPage="BusinessAvailabilityPage"
            desktopClassName={css.desktopTopbar}
            mobileClassName={css.mobileTopbar}
          />
          <UserNav selectedPageName="BusinessAvailabilityPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="BusinessAvailabilityPage" />
        <LayoutWrapperMain className={css.colorBackground}>
          <div className={css.content}>
            <div className={css.header}>
              <h1 className={css.title}>
                {intl.formatMessage({ id: 'BusinessAvailabilityPage.header', defaultMessage: 'Pivot dostępności sprzętu' })}
              </h1>
              <DatePicker onSubmit={handleDateRangeSubmit} value={{ startDate: start, endDate: end }} showComparison={false} />
            </div>
            <div className={css.filters}>
              <Select
                label="Wymiar 1"
                value={pendingDimensions[0]}
                onChange={e => handlePendingDimensionChange(0, e.target.value)}
              >
                {getOptions(0).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
              <Select
                label="Wymiar 2"
                value={pendingDimensions[1]}
                onChange={e => handlePendingDimensionChange(1, e.target.value)}
                disabled={!pendingDimensions[0]}
              >
                {getOptions(1).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
              <Select
                label="Wymiar 3"
                value={pendingDimensions[2]}
                onChange={e => handlePendingDimensionChange(2, e.target.value)}
                disabled={!pendingDimensions[1]}
              >
                {getOptions(2).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
              <button onClick={handleFiltersSubmit} style={{ marginLeft: 16, padding: '6px 18px', fontWeight: 600 }}>ZATWIERDŹ</button>
            </div>
            <div style={{ marginTop: 32 }}>
              {loading && <div>Ładowanie...</div>}
              {error && <div style={{ color: 'red' }}>{error}</div>}
              {pivot?.rows?.length > 0 && (
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table className={css.pivotTable} style={{ minWidth: stickyCount * 140 + (pivot?.dates?.length || 0) * 32 }}>
                    <thead>
                      <tr>
                        {pivot?.dims?.map((dim, i) => (
                          <th
                            key={dim}
                            className={classNames(css.pivotSticky, css[`pivotSticky${i+1}`])}
                            style={{ left: `${i * 140}px`, zIndex: 5, top: 0 }}
                          >
                            {DIMENSIONS.find(d => d.value === dim)?.label || dim}
                          </th>
                        ))}
                        {pivot?.dates?.map((date, colIdx) => {
                          const d = new Date(date);
                          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                          return (
                            <th
                              key={date}
                              className={classNames(
                                css.pivotDateHeader,
                                { [css.pivotWeekend]: isWeekend },
                                { [css.pivotColHover]: hoveredCol === colIdx }
                              )}
                              style={{ top: 0, zIndex: 6, writingMode: 'vertical-rl', width: 32, minWidth: 32, maxWidth: 32 }}
                              onMouseEnter={() => setHoveredCol(colIdx)}
                              onMouseLeave={() => setHoveredCol(null)}
                            >
                              {date.slice(8, 10) + '.' + date.slice(5, 7)}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {pivot?.rows?.map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className={classNames({ [css.pivotRowHover]: hoveredRow === rowIdx })}
                          onMouseEnter={() => setHoveredRow(rowIdx)}
                          onMouseLeave={() => setHoveredRow(null)}
                        >
                          {row.dims.map((val, i) => (
                            <td
                              key={i}
                              className={classNames(css.pivotSticky, css[`pivotSticky${i+1}`], { [css.pivotRowHover]: hoveredRow === rowIdx })}
                              style={{ left: `${i * 140}px`, zIndex: 5, background: '#fff' }}
                            >
                              {val}
                            </td>
                          ))}
                          {pivot?.dates?.map((date, colIdx) => {
                            const d = new Date(date);
                            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                            return (
                              <td
                                key={date}
                                className={classNames(
                                  { [css.pivotRowHover]: hoveredRow === rowIdx },
                                  { [css.pivotColHover]: hoveredCol === colIdx },
                                  { [css.pivotWeekendCell]: isWeekend }
                                )}
                                onMouseEnter={() => setHoveredCol(colIdx)}
                                onMouseLeave={() => setHoveredCol(null)}
                                style={{ width: 32, minWidth: 32, maxWidth: 32, textAlign: 'center' }}
                              >
                                {(row.values[date]?.booked ?? 0) + ' / ' + (row.values[date]?.available ?? 0)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!loading && !error && pivot?.rows?.length === 0 && <div>Brak danych do wyświetlenia.</div>}
            </div>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

BusinessAvailabilityPageComponent.defaultProps = {
  currentUser: null,
};

const { bool } = PropTypes;

BusinessAvailabilityPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, userProviders: currentUserProviders } = state.user;
  return {
    currentUser,
    currentUserProviders,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const loadData = params => (dispatch, getState, sdk) => {
  return sdk.newSdk.analyticsDays.query({ ...params })
    .then(res => res)
    .catch(e => { throw e; });
};

const mapDispatchToProps = dispatch => ({
  onLoadData: (params) => dispatch(loadData(params))
});

const BusinessAvailabilityPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl,
  withRouter
)(BusinessAvailabilityPageComponent);

export default BusinessAvailabilityPage;
